import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Feather, Hourglass, Network, Wind, Layers, Compass, ChevronLeft, ChevronRight } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { SpiralCinematic } from "@/components/SpiralCinematic";
import { CinematicVideo } from "@/components/CinematicVideo";
import { CocoonMark } from "@/components/CocoonMark";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";

const ICONS = [Feather, Hourglass, Network, Wind, Layers];
const NUMS = ["I", "II", "III", "IV", "V"];

function ManifesteCarousel({ articles }) {
  const [api, setApi] = useState(null);
  const [selected, setSelected] = useState(0);
  const [count, setCount] = useState(0);
  const hovering = useRef(false);

  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setSelected(api.selectedScrollSnap());
    const onSel = () => setSelected(api.selectedScrollSnap());
    api.on("select", onSel);
    api.on("reInit", onSel);
    return () => { api.off("select", onSel); };
  }, [api]);

  useEffect(() => {
    if (!api) return;
    const id = setInterval(() => { if (!hovering.current) api.scrollNext(); }, 6000);
    return () => clearInterval(id);
  }, [api]);

  return (
    <Carousel
      setApi={setApi}
      opts={{ loop: true, align: "center" }}
      onMouseEnter={() => { hovering.current = true; }}
      onMouseLeave={() => { hovering.current = false; }}
      className="relative"
      data-testid="manifeste-carousel"
    >
      <CarouselContent>
        {articles.map((a, i) => {
          const Icon = ICONS[i];
          return (
            <CarouselItem key={i} data-testid={`manifeste-slide-${NUMS[i]}`}>
              <motion.article
                initial={{ opacity: 0.4 }} animate={{ opacity: selected === i ? 1 : 0.4 }} transition={{ duration: 0.5 }}
                className="glass frame-pulse rounded-3xl p-8 md:p-12 flex flex-col sm:flex-row gap-6 min-h-[360px] sm:min-h-[320px]"
              >
                <div className="flex-shrink-0 flex sm:flex-col items-center gap-3">
                  <span className="font-display text-5xl md:text-6xl text-primary/70">{NUMS[i]}</span>
                  <Icon className="text-primary/60" size={26} strokeWidth={1.5} />
                </div>
                <div className="flex flex-col justify-center">
                  <h2 className="font-display text-2xl md:text-3xl mb-4 leading-snug">{a.title}</h2>
                  <p className="text-foreground/65 leading-[1.9] text-base md:text-lg">{a.body}</p>
                </div>
              </motion.article>
            </CarouselItem>
          );
        })}
      </CarouselContent>

      <div className="flex items-center justify-center gap-5 mt-9">
        <button
          onClick={() => api?.scrollPrev()}
          data-testid="manifeste-carousel-prev"
          aria-label="Précédent"
          className="w-10 h-10 rounded-full border border-primary/25 bg-primary/[0.06] text-primary flex items-center justify-center transition-all duration-300 hover:border-primary/60 hover:-translate-x-0.5"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-2.5">
          {Array.from({ length: count }).map((_, i) => (
            <button
              key={i}
              onClick={() => api?.scrollTo(i)}
              data-testid={`manifeste-dot-${i}`}
              aria-label={`Article ${NUMS[i]}`}
              className={`h-2 rounded-full transition-all duration-400 ${selected === i ? "w-8 bg-primary" : "w-2 bg-primary/30 hover:bg-primary/50"}`}
            />
          ))}
        </div>
        <button
          onClick={() => api?.scrollNext()}
          data-testid="manifeste-carousel-next"
          aria-label="Suivant"
          className="w-10 h-10 rounded-full border border-primary/25 bg-primary/[0.06] text-primary flex items-center justify-center transition-all duration-300 hover:border-primary/60 hover:translate-x-0.5"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </Carousel>
  );
}

export default function Manifeste() {
  const { t } = useTranslation();
  const m = t("manifeste", { returnObjects: true });

  return (
    <div className="relative min-h-screen bg-background text-foreground overflow-x-clip">
      <div className="grain-overlay" aria-hidden />
      <div className="amber-glow" aria-hidden style={{ top: "-8%", left: "50%", transform: "translateX(-50%)", width: "880px", height: "640px" }} />
      <div className="amber-glow" aria-hidden style={{ top: "62%", left: "-14%", width: "620px", height: "620px", opacity: 0.6 }} />
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" className="font-display text-xl sm:text-2xl tracking-tight whitespace-nowrap" data-testid="manifeste-brand"><CocoonMark withMind /></Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <LanguageSwitcher className="hidden sm:flex" />
            <Link to="/echo" className="text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.echo")}</Link>
            <Link to="/tarifs" className="text-sm text-foreground/60 hover:text-primary transition-colors">{t("nav.tarifs")}</Link>
            <Link to="/entrer" data-testid="manifeste-enter-btn" className="btn-frost-orange text-sm font-semibold px-4 sm:px-6 py-2 rounded-full">{t("nav.entrer")}</Link>
          </div>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 pt-40 pb-16 text-center">
        <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="text-primary/80 tracking-[0.3em] uppercase text-xs mb-8">{m.eyebrow}</motion.p>
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15 }} className="max-w-3xl mx-auto">
          <CinematicVideo
            fade
            src="https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/rjatc6t2_gemini_generated_video_f109e4ca.mp4"
            testId="manifeste-cinematic-video"
            caption={`${m.quoteA}${m.quoteHi1}${m.quoteMid}${m.quoteHi2}${m.quoteB}`}
          />
        </motion.div>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-16" data-testid="manifeste-articles">
        <ManifesteCarousel articles={m.articles} />
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-28 text-center">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }}>
          <p className="font-display italic text-3xl sm:text-4xl leading-relaxed mb-3">{m.closingA}</p>
          <p className="font-display italic text-2xl text-foreground/60 mb-10">
            {m.closingB}<br />{m.closingC}<span className="text-primary">{m.closingHi}</span>.
          </p>
          <Link to="/entrer" data-testid="manifeste-cta-btn" className="btn-frost-orange inline-flex items-center gap-3 px-9 py-4 rounded-full font-semibold text-lg">
            {m.closingCta} <Compass size={18} />
          </Link>
        </motion.div>
      </section>

      <footer className="border-t border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row justify-between gap-4 text-sm text-foreground/40">
          <Link to="/" className="font-display text-lg"><CocoonMark letterClassName="text-foreground/60" withMind /></Link>
          <span>{t("landing.footerTagline")}</span>
        </div>
      </footer>
    </div>
  );
}
